// This function will calcualate the payments owed by each indivdual who placed an
// order and store it in the database to be tracked.
import firebase from "../../../components/Firebase.js";
import PriceTotals from "../../../functions/PriceTotals.js";
import PackageTotals from "../../../functions/PackageTotals.js";
import CreatePaymentData from "../../../functions/CreatePaymentData.js";

export default function CreatePaymentHistory(
  now,
  selectedDate,
  importedOrder,
  communityOrders,
  userInfo,
  addToExistingOrder,
  ordersPaymentHistoryExisting,
  docName,
) {
  // This dictionary is used to store the data in the database including.
  //  selectedDate : MM-DD-YYYY
  //   importedOrder :
  // 	  DistributionLocation :
  // 		 Location : XXXX
  // 	   date : MM-DD-YYYY
  // 	   communityOrders :
  //      firstName-lastName-userId-date :
  //      deposits: { timestamp : $XX.XX, timestamp : $XX.XX,
  //                            timestamp : $XX.XX}
  //      totalOwed : $XX.XX
  //      DistributionLocation : XXXX
  //      Owed History : [timestamp : $XX.XX, timestamp : $XX.XX, timestamp : $XX.XX]
  //      OrderNotes : XXXXXX
  //      orderRefunds :{ timestamp : $XX.XX, timestamp : $XX.XX,
  //                      timestamp : $XX.XX}
  // 	 communityOrders
  // 		firstName-lastName-userId-date :
  //      deposits: { timestamp : $XX.XX, timestamp : $XX.XX,
  //                            timestamp : $XX.XX}
  //      totalOwed : $XX.XX
  //      DistributionLocation : XXXX
  //      Owed History : [timestamp : $XX.XX, timestamp : $XX.XX, timestamp : $XX.XX]
  //      OrderNotes : XXXXXX
  //      orderRefunds :{ timestamp : $XX.XX, timestamp : $XX.XX,
  //                      timestamp : $XX.XX}
  // Set up the datebase.
  const database = firebase.firestore();
  const batch = database.batch();

  let orderPaymentHistory = {};
  const orderExistsField = now.concat("Exists");
  // The dictionary that saves the community orders payment history.  This is used
  // later and added into the larger payment history that is saved to the database.  This
  // is specifically for the community location's orders not their imported orders.
  const communityOrdersPaymentHistory = {};
  // The community orders dictionary needs to be initialized.
  communityOrdersPaymentHistory.communityOrders = {};

  // If the distribution location is adding to an existing order then we don't
  // want to overwrite the payment history that already exists.
  if (addToExistingOrder) {
    communityOrdersPaymentHistory.communityOrders =
      ordersPaymentHistoryExisting.communityOrders;
  }

  // Since we want to order the user's order history from oldest to newest we have to
  // sort the order.
  const userOrders = Object.keys(communityOrders).slice();
  userOrders.sort();

  // Cycle through all the communityOrders
  userOrders.forEach((userOrder) => {
    // Total up the cost of the order using PriceTotals which separates out the total
    // owed to each farm.
    const orderFarmsCost = PriceTotals(
      communityOrders[userOrder].foodList,
      true,
    );

    // A dictionary of the the farms(keys) and the total packaging fees to them (values)
    const packageTotals = PackageTotals(communityOrders[userOrder].foodList);
    // set the order cost to 0.
    let orderCost = 0;
    // Cycle through the totals owed to each farm and add them up.
    Object.keys(orderFarmsCost).forEach((farmName) => {
      orderCost += orderFarmsCost[farmName];
      if (packageTotals[farmName] > 0) {
        orderCost += packageTotals[farmName];
      }
    });
    // Add or subtract the subsidy and donations to the total order cost.
    if (communityOrders[userOrder].customerContribution === "donation") {
      orderCost += communityOrders[userOrder].donationSubsidy;
    } else {
      orderCost -= communityOrders[userOrder].donationSubsidy;
    }

    // If there is a cash portion for the volunteer fee then add it to the total
    // order cost.
    if (communityOrders[userOrder].cashPortion) {
      orderCost += parseFloat(communityOrders[userOrder].cashPortion);
    }

    // Split the user's name up to remove the date of the order as that is stored in
    // the orderHistory key.
    const user = [
      userOrder.split("-")[0],
      userOrder.split("-")[1],
      userOrder.split("-")[2],
    ].join("-");
    // Store the date of the user's order separately to be used later.
    const userOrderTime = userOrder.split("-")[3];

    // The order hasn't been added to the payment history yet add it.
    if (
      !communityOrdersPaymentHistory.communityOrders[user]?.orderHistory?.[
        userOrderTime
      ]
    ) {
      // Either the user doesn't exist yet, or this order time isn't recorded
      communityOrdersPaymentHistory.communityOrders[user] = CreatePaymentData(
        communityOrdersPaymentHistory.communityOrders[user],
        orderCost,
        userOrderTime,
        userInfo.organisationName,
        communityOrders[userOrder].paymentMethod,
      );
    }
  });

  // Update the payment history to include the community orders.
  orderPaymentHistory = communityOrdersPaymentHistory;

  // Saved selectedDate.
  orderPaymentHistory.selectedDate = selectedDate;

  // This is the same as above but includes the imported communty order history instead
  // including some additional information about the imported location.
  let importedCommunityOrdersPaymentHistory = {};

  // If the distribution location is adding to an existing order then we don't
  // want to overwrite the payment history that already exists.
  if (addToExistingOrder) {
    importedCommunityOrdersPaymentHistory =
      ordersPaymentHistoryExisting.importedOrder;
  }

  // Update the payment history to include the community orders from all the imported
  // locations.
  orderPaymentHistory.importedOrder = importedCommunityOrdersPaymentHistory;

  // Checks if we're just adding to an existing order.  If we are then we can just
  // update the orderPaymentHistory with our changes.
  if (addToExistingOrder) {
    const newPaymentHistoryDocRef = database
      .collection("PaymentHistory")
      .doc(docName);

    // Update the payment history
    return newPaymentHistoryDocRef
      .update({
        [now]: orderPaymentHistory,
      })
      .catch((error) => {
        // The document probably doesn't exist.
        console.error("Error updating document: ", error);
      });
  }
  // If we aren't adding to an existing order then we need to create a new payment
  // history.

  // Find the most recent payment history.
  const paymentHistoryDocRef = database
    .collection("PaymentHistory")
    .orderBy("createdDate", "desc")
    .limit(1);
  // Update the Payment History to store information to help track payments.
  paymentHistoryDocRef
    .get()
    .then((collection) => {
      // If the collection is 0 length it means that we didn't already have a
      // payment history saved and we need to create a new document.
      if (collection.docs.length === 0) {
        const newPaymentHistoryDocRef = database
          .collection("PaymentHistory")
          .doc(now);

        batch.set(newPaymentHistoryDocRef, {
          [now]: orderPaymentHistory,
          createdDate: now,
          [orderExistsField]: true,
        });
      }
      // Read the existing document.
      collection.docs.forEach((doc) => {
        // If the document exists count how many current orders are saved in this
        // document.  There should be two fields for every order one with the order
        // payment history and one with the "Exists" boolean.
        if (doc.exists) {
          let orderCount = 0;
          Object.keys(doc.data()).forEach((order) => {
            if (order !== "createdDate") {
              orderCount += 1;
            }
          });
          // If there are already 5 orders then we have to create a new document.
          // We use 10 instead of 5 because each order has the payment history and the
          // orderDate + "Exists" field so there are two keys for every 1 order.
          if (orderCount >= 10) {
            // Create a new document with the name of now.
            const newPaymentHistoryDocRef = database
              .collection("PaymentHistory")
              .doc(now);
            // Set the document to be equal to the current payment history.
            batch.set(newPaymentHistoryDocRef, {
              [now]: orderPaymentHistory,
              createdDate: now,
              [orderExistsField]: true,
            });
          }
          // If there are less than 5 then we can add this order to the last created
          // document.
          else {
            // Set the dataTransfer equal to the data in the document.
            const dataTransfer = doc.data();
            // Add the new orderPaymentHistory that was just created.
            dataTransfer[now] = orderPaymentHistory;
            dataTransfer[orderExistsField] = true;
            const createdDate = dataTransfer.createdDate;

            const newPaymentHistoryDocRef = database
              .collection("PaymentHistory")
              .doc(createdDate);

            // set the document to be equal to the nw updated orderPaymentHistory.
            // newPaymentHistoryDocRef.set(dataTransfer)
            // Set the document to be equal to the current payment history.
            batch.set(newPaymentHistoryDocRef, dataTransfer);
          }
        } else {
          const newPaymentHistoryDocRef = database
            .collection("PaymentHistory")
            .doc(now);

          batch.set(newPaymentHistoryDocRef, {
            [now]: orderPaymentHistory,
            createdDate: now,
            [orderExistsField]: true,
          });
        }
      });
      batch.commit();
    })
    .catch(function (error) {
      console.log("Error getting document:", error);
    });
}
